import React, { createContext, useContext, useMemo, useRef } from 'react'

type Subscribers<T> = Map<keyof T, Set<(event: unknown) => void>>

type SubscribeType<T> = <Key extends string & keyof T>(
  eventType: Key,
  callback: (event: T[Key]) => void,
) => () => void

type TriggerType<T> = <Key extends string & keyof T>(
  eventType: Key,
  event: T[Key],
) => void

export interface EventObserverContextValue<T> {
  trigger: TriggerType<T>
  subscribe: SubscribeType<T>
}

export class EventBus<T> {
  private allSubscribers: Subscribers<T> = new Map()

  subscribe: SubscribeType<T> = (eventName, callback) => {
    const subscribers = this.allSubscribers.get(eventName) ?? new Set()
    subscribers.add(callback as (event: unknown) => void)
    this.allSubscribers.set(eventName, subscribers)

    return () => this.unsubscribe(eventName, callback)
  }

  unsubscribe = <Key extends string & keyof T>(
    eventName: Key,
    callback: (event: T[Key]) => void,
  ) => {
    const subscribers = this.allSubscribers.get(eventName)
    if (!subscribers) return

    subscribers.delete(callback as (event: unknown) => void)

    if (subscribers.size === 0) {
      this.allSubscribers.delete(eventName)
    }
  }

  trigger: TriggerType<T> = (eventName, event) => {
    const subscribers = this.allSubscribers.get(eventName)
    if (!subscribers) return

    /*
    Make a copy of the subscriber list to iterate on, as it appears to be
    possible that effects can run before the forEach call completes which
    can unsubscribe and resubscribe and then be executed again, resulting
    in an infinite loop.
    */
    const copy = Array.from(subscribers)
    copy.forEach((sub) => sub(event))
  }
}

type EventsObserverProviderProps = {
  children: React.ReactNode
}

/**
 * Factory for creating an event context/provider and hook for event pub/sub
 *
 * @param contextName (optional) Used only in error messages
 * @returns Provider, Context, and hook for subscribing to and triggering events
 */
export const createEventBus = <T,>(contextName = 'EventProvider') => {
  const EventsObserverContext =
    createContext<EventObserverContextValue<T> | null>(null)

  const EventsProvider = ({ children }: EventsObserverProviderProps) => {
    const eventBus = useRef(new EventBus<T>()).current
    const value = useMemo(
      () => ({
        subscribe: eventBus.subscribe,
        trigger: eventBus.trigger,
      }),
      [eventBus],
    )

    return (
      <EventsObserverContext.Provider value={value}>
        {children}
      </EventsObserverContext.Provider>
    )
  }

  const useEventBusEvents = () => {
    const ctx = useContext(EventsObserverContext)
    if (!ctx)
      throw new Error(
        `Did you forget to wrap your component in <${contextName}>?`,
      )
    return ctx
  }

  return {
    EventsProvider,
    EventsObserverContext,
    useEventBusEvents,
  }
}
